import { logger } from '@adornis/base/logging.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { baseqlMetaData } from '@adornis/baseql/metadata/metaDataStore.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { catchError, from, map, of, switchMap } from 'rxjs';
import { TravelCertificate } from '../LogistikProduct.js';
import { LogistikProductFilter } from '../LogistikProductFilter.js';

const subscribeTravelCertificatesPaginated = registerSubscription({
  type: () => [TravelCertificate],
  operationName: 'subscribeTravelCertificatesPaginated',
  options: {},
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => LogistikProductFilter, name: 'filter' },
  ],
  resolve: (skip: number, limit: number, filter: LogistikProductFilter) => {
    return (gqlFields: BaseQLSelectionSet<TravelCertificate>) => {
      const selector = filter?.getMongoFilter() ?? {};
      const a = from(publishFilter(TravelCertificate._class)).pipe(
        switchMap(pf => {
          return watchQuery<TravelCertificate>(
            TravelCertificate._collectionName,
            pf
              ? {
                  $and: [
                    pf,
                    selector,
                    { isPublished: { $ne: false } },
                    { _class: { $in: baseqlMetaData.entities.validSubclasses(TravelCertificate) } },
                  ],
                }
              : {
                  selector,
                  isPublished: { $ne: false },
                  _class: { $in: baseqlMetaData.entities.validSubclasses(TravelCertificate) },
                },
            {
              sort: filter.getMongoOptions().sort,
              skip: skip,
              limit: limit,
            },
          );
        }),
        catchError(err => {
          logger.error({ err }, 'Fehler in subscribeTravelCertificatesPaginated');
          return of([] as TravelCertificate[]);
        }),
      );
      return a;
    };
  },
});

const subscribeTravelCertificateCount = registerSubscription({
  type: () => Int,
  operationName: 'subscribeTravelCertificateCount',
  options: {},
  params: [],
  resolve: () => {
    return () => {
      return watchQuery(
        TravelCertificate._collectionName,
        {
          _class: { $in: baseqlMetaData.entities.validSubclasses(TravelCertificate) },
          isPublished: { $ne: false },
        },
        { projection: { _id: 1 } },
      ).pipe(map(tcs => tcs.length));
    };
  },
});

export namespace TravelCertificateAPI {
  export const subscribePaginated = subscribeTravelCertificatesPaginated;
  export const count = subscribeTravelCertificateCount;
}
